import React, { useMemo } from "react";

import { Group, Rect, Text } from "react-konva";

import {
  BackgroundStyle,
  LetterCase,
  REFERENCE_PREVIEW_HEIGHT,
  SubtitleStyles,
} from "../../types";
import { Caption, activeWordIndex } from "../../utils/captionGrouping";

interface CaptionRendererProps {
  caption: Caption;
  timeSec: number;
  styles: SubtitleStyles;
  videoDimensions: { width: number; height: number };
  handlePositionX: number;
  handlePositionY: number;
  handleWidth: number;
}

interface PlacedWord {
  index: number;
  text: string;
  x: number;
  y: number;
  width: number;
}

const measureContext = document.createElement("canvas").getContext("2d");

const applyCase = (text: string, letterCase?: LetterCase) => {
  if (letterCase === LetterCase.UPPERCASE) return text.toUpperCase();
  if (letterCase === LetterCase.LOWERCASE) return text.toLowerCase();
  if (letterCase === LetterCase.CAPITALIZE) {
    return text.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return text;
};

const easeOutBack = (p: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (p - 1) ** 3 + c1 * (p - 1) ** 2;
};

// Mirrors the \t scale steps written by the export so preview and video match.
const POP_PEAK = 1.2;
const POP_REST = 1.12;
const popScale = (sinceStart: number) => {
  if (sinceStart < 0.1) return 1 + (POP_PEAK - 1) * (sinceStart / 0.1);
  if (sinceStart < 0.2) {
    return POP_PEAK - (POP_PEAK - POP_REST) * ((sinceStart - 0.1) / 0.1);
  }
  return POP_REST;
};

// Extra room between two words so whichever of them pops never covers the
// other. Centered text grows both ways, left/right aligned text grows away
// from its edge. Keep in sync with _pop_gap in the export's subtitles.py.
const popGap = (a: number, b: number, textAlign?: string) =>
  (POP_PEAK - 1) *
  Math.max(a, b) *
  (textAlign === "left" || textAlign === "right" ? 1 : 0.5);

export default function CaptionRenderer({
  caption,
  timeSec,
  styles,
  videoDimensions,
  handlePositionX,
  handlePositionY,
  handleWidth,
}: CaptionRendererProps) {
  const animation = styles.caption_animation ?? "none";
  const scale = videoDimensions.height / REFERENCE_PREVIEW_HEIGHT;
  const fontSize = Math.round((styles.size || 24) * scale);
  const padding = Math.round(20 * scale);
  const lineHeight = fontSize * (styles.line_height || 1.2);
  const letterSpacing = styles.letter_spacing ?? 0;
  const fontFamily = styles.font || "Inter";
  const fontStyle =
    [styles.italic && "italic", styles.bold && "bold"]
      .filter(Boolean)
      .join(" ") || "normal";

  const boxX = (handlePositionX / 100) * videoDimensions.width;
  const boxY = (handlePositionY / 100) * videoDimensions.height;
  const boxWidth = Math.max(0, (handleWidth / 100) * videoDimensions.width);

  const layout = useMemo(() => {
    const font = `${fontStyle === "normal" ? "" : `${fontStyle} `}${fontSize}px "${fontFamily}"`;
    const measure = (text: string) => {
      if (!measureContext) return text.length * fontSize * 0.6;
      measureContext.font = font;
      return (
        measureContext.measureText(text).width + letterSpacing * text.length
      );
    };
    const spaceWidth = measure(" ");
    const gap = (a: number, b: number) =>
      spaceWidth + (animation === "pop" ? popGap(a, b, styles.text_align) : 0);
    const innerWidth = Math.max(10, boxWidth - padding * 2);

    const lines: { index: number; text: string; width: number }[][] = [[]];
    let lineWidth = 0;
    caption.words.forEach((w, index) => {
      const text = applyCase(w.word, styles.letter_case);
      const width = measure(text);
      const current = lines[lines.length - 1];
      const last = current[current.length - 1];
      const nextWidth = last
        ? lineWidth + gap(last.width, width) + width
        : width;
      if (current.length && nextWidth > innerWidth) {
        lines.push([{ index, text, width }]);
        lineWidth = width;
      } else {
        current.push({ index, text, width });
        lineWidth = nextWidth;
      }
    });

    const textHeight = lines.length * lineHeight;
    const rectHeight = Math.max(
      Math.round(60 * scale),
      textHeight + padding * 2,
    );
    const top = (rectHeight - textHeight) / 2;

    const placed: PlacedWord[] = [];
    lines.forEach((line, lineIndex) => {
      const width = line.reduce(
        (sum, w, i) =>
          sum + w.width + (i > 0 ? gap(line[i - 1].width, w.width) : 0),
        0,
      );
      let x = (boxWidth - width) / 2;
      if (styles.text_align === "left") x = padding;
      if (styles.text_align === "right") x = boxWidth - padding - width;
      line.forEach((w, i) => {
        placed.push({ ...w, x, y: top + lineIndex * lineHeight });
        x += w.width + (line[i + 1] ? gap(w.width, line[i + 1].width) : 0);
      });
    });
    return { placed, rectHeight };
  }, [
    caption,
    fontStyle,
    fontSize,
    fontFamily,
    letterSpacing,
    boxWidth,
    padding,
    lineHeight,
    scale,
    animation,
    styles.letter_case,
    styles.text_align,
  ]);

  const activeIndex = activeWordIndex(caption, timeSec);
  const baseColor = styles.color || "#FFFFFF";
  const highlight = styles.highlight_color || "#FACC15";
  const outline = (styles.outline_thickness ?? 0) * scale;
  const hasBackground =
    !!styles.background_style &&
    styles.background_style !== BackgroundStyle.NONE;

  const renderWord = (w: PlacedWord) => {
    const word = caption.words[w.index];
    const isActive = w.index === activeIndex;
    const sinceStart = timeSec - word.start;

    let fill = baseColor;
    if (animation === "karaoke" && w.index <= activeIndex) fill = highlight;
    if ((animation === "highlight" || animation === "pop") && isActive) {
      fill = highlight;
    }
    if (animation === "box" && isActive) {
      fill = styles.highlight_text_color || "#FFFFFF";
    }

    let wordScale = 1;
    if (animation === "pop" && isActive) wordScale = popScale(sinceStart);
    if (animation === "single_word") {
      wordScale =
        0.7 + 0.3 * easeOutBack(Math.min(1, Math.max(0, sinceStart / 0.12)));
    }

    const boxPadX = fontSize * 0.18;
    const boxHeight = fontSize * 1.2;

    // Grow from the same point the export does: the word's center for
    // centered text, its aligned edge otherwise.
    let originX = w.width / 2;
    if (styles.text_align === "left") originX = 0;
    if (styles.text_align === "right") originX = w.width;

    return (
      <Group
        key={w.index}
        x={w.x + originX}
        y={w.y + lineHeight / 2}
        offsetX={originX}
        offsetY={lineHeight / 2}
        scaleX={wordScale}
        scaleY={wordScale}
        listening={false}
      >
        {animation === "box" && isActive && (
          <Rect
            x={-boxPadX}
            y={(lineHeight - boxHeight) / 2}
            width={w.width + boxPadX * 2}
            height={boxHeight}
            fill={highlight}
            cornerRadius={Math.max(1, fontSize * 0.04)}
          />
        )}
        <Text
          text={w.text}
          width={w.width + letterSpacing + 2}
          height={lineHeight}
          verticalAlign="middle"
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontStyle={fontStyle}
          textDecoration={styles.underline ? "underline" : ""}
          letterSpacing={letterSpacing}
          fill={fill}
          stroke={outline > 0 ? styles.outline_color || "#000000" : undefined}
          strokeWidth={outline * 2}
          fillAfterStrokeEnabled
          lineJoin="round"
          wrap="none"
        />
      </Group>
    );
  };

  return (
    <Group x={boxX} y={boxY} listening={false}>
      {hasBackground && (
        <Rect
          width={boxWidth}
          height={layout.rectHeight}
          fill={styles.background_color || "black"}
          opacity={0.8}
          cornerRadius={
            styles.background_style === BackgroundStyle.ROUNDED ? 10 : 0
          }
        />
      )}
      {layout.placed.map(renderWord)}
    </Group>
  );
}
